import appStore from '../utils/state';
import gsap from 'gsap';

export default class OutOfStockModal extends HTMLElement {
  private showTl: gsap.core.Timeline | null = null;
  private hideTl: gsap.core.Timeline | null = null;

  private dialog: HTMLDialogElement | null = null;
  private closeButton: HTMLButtonElement | null = null;

  private triggerElement: HTMLElement | null = null;

  private unsubscribeFromOutOfStockModalOpenState: (() => void) | null = null;

  private onOutOfStockModalToggleBound = this.onOutOfStockModalToggle.bind(this);
  private onCloseButtonClickBound = this.onCloseButtonClick.bind(this);
  private onDialogCancelBound = this.onDialogCancel.bind(this);
  private onDialogCloseBound = this.onDialogClose.bind(this);
  private onDialogClickBound = this.onDialogClick.bind(this);

  connectedCallback(): void {
    this.dialog = this.querySelector('dialog');
    this.closeButton = this.querySelector('[data-out-of-stock-modal-ref="close-button"]');

    this.addEventListeners();
    this.subscribe();
  }

  addEventListeners(): void {
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.onCloseButtonClickBound);
    }
    if (this.dialog) {
      this.dialog.addEventListener('cancel', this.onDialogCancelBound);
      this.dialog.addEventListener('close', this.onDialogCloseBound);
      this.dialog.addEventListener('click', this.onDialogClickBound);
    }
  }

  removeEventListeners(): void {
    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.onCloseButtonClickBound);
    }
    if (this.dialog) {
      this.dialog.removeEventListener('cancel', this.onDialogCancelBound);
      this.dialog.removeEventListener('close', this.onDialogCloseBound);
      this.dialog.removeEventListener('click', this.onDialogClickBound);
    }
  }

  subscribe(): void {
    this.unsubscribeFromOutOfStockModalOpenState = appStore.subscribeKey(
      'isOutOfStockModalOpen',
      this.onOutOfStockModalToggleBound,
    );
  }

  unsubscribe(): void {
    if (this.unsubscribeFromOutOfStockModalOpenState) {
      this.unsubscribeFromOutOfStockModalOpenState();
    }
  }

  onCloseButtonClick(): void {
    appStore.actions.closeOutOfStockModal();
  }

  onDialogCancel(event: Event): void {
    event.preventDefault();
    appStore.actions.closeOutOfStockModal();
  }

  onDialogClick(event: MouseEvent): void {
    if (event.target === this.dialog) {
      appStore.actions.closeOutOfStockModal();
    }
  }

  onDialogClose(): void {
    if (appStore.getState().isOutOfStockModalOpen) {
      appStore.actions.closeOutOfStockModal();
    }

    this.restoreFocus();
  }

  captureTrigger(): void {
    const trigger = document.querySelector('[aria-controls="out-of-stock-modal-dialog"]');

    if (trigger instanceof HTMLElement) {
      this.triggerElement = trigger;
      return;
    }

    const activeElement = document.activeElement;

    if (activeElement instanceof HTMLElement && !this.contains(activeElement)) {
      this.triggerElement = activeElement;
    }
  }

  restoreFocus(): void {
    if (this.triggerElement && document.contains(this.triggerElement)) {
      this.triggerElement.focus();
    }

    this.triggerElement = null;
  }

  onOutOfStockModalToggle(isOutOfStockModalOpen: boolean): void {
    if (isOutOfStockModalOpen) {
      this.show();
    } else {
      this.hide();
    }
  }

  show(): void {
    if (!this.dialog) {
      return;
    }

    if (this.hideTl) {
      this.hideTl.kill();
    }
    if (this.showTl) {
      this.showTl.kill();
    }

    gsap.set(this.dialog, { autoAlpha: 0, visibility: 'visible' });

    this.captureTrigger();

    if (!this.dialog.open) {
      this.dialog.showModal();
    }

    this.showTl = gsap.timeline();
    this.showTl.fromTo(this.dialog, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, ease: 'power2.out' }, 0);
    this.showTl.fromTo(this.dialog, { x: '5%' }, { x: 0, force3D: true, duration: 0.7, ease: 'expo.out' }, 0);
  }

  hide(): void {
    if (!this.dialog) {
      return;
    }

    if (this.showTl) {
      this.showTl.kill();
    }
    if (this.hideTl) {
      this.hideTl.kill();
    }

    this.hideTl = gsap.timeline({
      onComplete: () => {
        if (this.dialog?.open) {
          this.dialog.close();
        }
      },
    });

    this.hideTl.to(this.dialog, { autoAlpha: 0, duration: 0.2, ease: 'power2.out' }, 0);
    this.hideTl.to(this.dialog, { x: '5%', duration: 0.3, force3D: true, ease: 'expo.out' }, 0);
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
    this.unsubscribe();
  }
}

customElements.define('out-of-stock-modal', OutOfStockModal);
